/**
 * Public client preview (iter-78). No auth required — token-scoped.
 * Client can:
 *   • Read the book preview (watermarked PDF in an iframe)
 *   • Leave comments
 *   • Download a watermarked PDF once the admin approves
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function ClientPreview() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");

  const reloadComments = async () => {
    try {
      const r = await fetch(`${API}/client-preview/${token}/comments`);
      if (r.ok) setComments((await r.json()).comments || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/client-preview/${token}`);
        if (!r.ok) throw new Error((await r.json()).detail || "Preview link not available");
        const data = await r.json();
        setMeta(data);
        document.title = `${data.title} — Preview · BookForge360`;
        await reloadComments();
      } catch (err) { setError(err.message || "Preview link not available"); }
    })();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const postComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const r = await fetch(`${API}/client-preview/${token}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: comment.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed to post");
      setComment("");
      await reloadComments();
      toast.success("Comment sent to the author");
    } catch (err) { toast.error(err.message); }
    finally { setPosting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#f6f4ee] flex items-center justify-center p-6">
        <Toaster position="bottom-right" richColors />
        <div className="max-w-md bg-white border border-[color:var(--border)] p-8" data-testid="client-preview-error">
          <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">§ Preview link</p>
          <h1 className="font-editorial text-3xl mt-3">This preview isn't available.</h1>
          <p className="text-sm text-[color:var(--text-muted)] mt-3">{error}</p>
          <p className="text-sm text-[color:var(--text-muted)] mt-2">
            The author may have removed access or regenerated the link. Reply to
            the email that sent you here, or write to{" "}
            <a href="mailto:support@bookforge360.com" className="underline">support@bookforge360.com</a>.
          </p>
        </div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="min-h-screen bg-[#f6f4ee] flex items-center justify-center">
        <p className="text-sm text-[color:var(--text-muted)]">Loading preview…</p>
      </div>
    );
  }

  const downloadUrl = `${API}/client-preview/${token}/download/pdf`;
  const previewPdfUrl = `${API}/client-preview/${token}/preview.pdf`;

  return (
    <div className="min-h-screen bg-[#f6f4ee]" data-testid="client-preview-page">
      <Toaster position="bottom-right" richColors />
      <header className="max-w-5xl mx-auto px-6 pt-10 pb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
              § Private preview · BookForge360
            </p>
            <h1 className="font-editorial text-4xl mt-2" data-testid="client-preview-title">
              {meta.title}
            </h1>
            {meta.subtitle && <p className="text-lg text-[color:var(--text-muted)] italic mt-1">{meta.subtitle}</p>}
            {meta.author && <p className="text-sm text-[color:var(--text-muted)] mt-1">by {meta.author}</p>}
          </div>
          <div className="text-right">
            {meta.download_approved ? (
              <a href={downloadUrl} className="btn-primary text-sm"
                  data-testid="client-preview-download">Download watermarked PDF</a>
            ) : (
              <div className="text-xs bg-white border border-[color:var(--border)] px-3 py-2"
                    data-testid="client-preview-download-locked">
                <p className="font-medium">Downloads locked</p>
                <p className="text-[color:var(--text-muted)] mt-1">
                  The author will approve when the draft is ready to hand off.
                </p>
              </div>
            )}
          </div>
        </div>
        <p className="text-[11px] text-[color:var(--text-muted)] mt-3">
          You're logged in as <span className="font-mono">{meta.client_email}</span> ·
          Every page carries a preview watermark. Please don't share this link.
        </p>
      </header>

      <div className="max-w-5xl mx-auto px-6 pb-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white border border-[color:var(--border)] min-h-[75vh]">
          {meta.has_pdf ? (
            <iframe title="Book preview" src={previewPdfUrl}
                    className="w-full h-[75vh]"
                    data-testid="client-preview-iframe"/>
          ) : (
            <div className="p-8 text-center text-sm text-[color:var(--text-muted)]"
                  data-testid="client-preview-empty">
              This book has no content yet. Please check back later.
            </div>
          )}
        </div>

        <aside className="bg-white border border-[color:var(--border)] p-4 h-fit sticky top-6"
                data-testid="client-preview-comments-panel">
          <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Comments</p>
          <h2 className="font-editorial text-lg mt-1">Leave feedback</h2>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">
            The author gets a notification with every note.
          </p>

          <form onSubmit={postComment} className="mt-3 space-y-2">
            <textarea value={comment} onChange={(e)=>setComment(e.target.value)}
                        placeholder="Type your thoughts…"
                        rows={4} maxLength={4000}
                        className="w-full border border-[color:var(--border)] p-2 text-sm"
                        data-testid="client-preview-comment-input"/>
            <button type="submit" disabled={posting || !comment.trim()}
                    className="btn-primary text-sm w-full"
                    data-testid="client-preview-comment-submit">
              {posting ? "Sending…" : "Send comment"}
            </button>
          </form>

          <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
            {comments.length === 0 && (
              <p className="text-xs text-[color:var(--text-muted)] italic">
                Your comments will show up here.
              </p>
            )}
            {comments.map((c) => (
              <div key={c.id} className="border-l-2 border-[color:var(--primary)] pl-3 py-1">
                <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
                  {new Date(c.created_at).toLocaleString()}
                </p>
                <p className="text-sm mt-1 whitespace-pre-wrap">{c.content}</p>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <footer className="max-w-5xl mx-auto px-6 pb-10 text-center">
        <p className="text-xs text-[color:var(--text-muted)]">
          Powered by <Link to="/" className="underline">BookForge360</Link> · Need help?{" "}
          <a href="mailto:support@bookforge360.com" className="underline">support@bookforge360.com</a>
        </p>
      </footer>
    </div>
  );
}
